import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { InventoryAdjustmentApi } from 'api/inventoryAdjustment';
import { UserContext } from 'contexts/UserContext';
import { Button } from 'shadcn/components/ui/Button';
import { Checkbox } from 'shadcn/components/ui/Checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'shadcn/components/ui/Dialog';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'shadcn/components/ui/Tabs';
import { Notifications } from 'shared/components/Notifications';

import { BulkShrinkShipmentUpload } from './BulkShrinkShipmentUpload';
import { BulkShrinkSummary } from './BulkShrinkSummary';

const inventoryAdjustmentApi = new InventoryAdjustmentApi();

function ShrinkShipmentModal({ isOpen, onOpenChange }) {
    const { user } = useContext(UserContext);
    const [activeTab, setActiveTab] = useState('single');
    const [shipmentId, setShipmentId] = useState('');
    const shipmentInputRef = useRef(null);
    const [isSearching, setIsSearching] = useState(false);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [rows, setRows] = useState([]);
    const [selectedRowIds, setSelectedRowIds] = useState(new Set());
    const reasonShipmentRepack = 'Shipment Repack';
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [notes, setNotes] = useState('');
    const [submissionResults, setSubmissionResults] = useState(null);

    const clearState = useCallback(() => {
        setActiveTab('single');
        setShipmentId('');
        setRows([]);
        setSelectedRowIds(new Set());
        setNotification({ text: null, level: null });
        setSubmissionResults(null);
        setNotes('');
    }, []);

    // Ensure summary is hidden on each open
    useEffect(() => {
        if (isOpen) {
            clearState();
        }
    }, [clearState, isOpen]);

    const toggleRow = useCallback((id) => {
        setSelectedRowIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const toggleAll = useCallback(
        (checked) => {
            if (checked) {
                setSelectedRowIds(new Set(rows.map((r) => r.id)));
            } else {
                setSelectedRowIds(new Set());
            }
        },
        [rows],
    );

    const handleClose = useCallback(() => {
        clearState();
        onOpenChange(false);
    }, [clearState, onOpenChange]);

    const handleQtyChange = useCallback((id, value) => {
        setRows((prev) =>
            prev.map((r) => (r.id === id ? { ...r, qty: value === '' ? '' : Math.max(0, Number(value)) } : r)),
        );
    }, []);

    const handleSearch = useCallback(async () => {
        const raw = shipmentInputRef.current?.value ?? shipmentId;
        const id = String(raw).trim();
        // Update state first so UI reflects the normalized value
        if (id !== shipmentId) setShipmentId(id);
        if (!id) return;
        setIsSearching(true);
        try {
            const response = await inventoryAdjustmentApi.getBulkShrinkItemsForShipment({
                facilityId: user.facilityID,
                shipmentID: id,
            });
            if (response?.status === false || response?.data?.items?.length === 0) {
                setNotification({ text: 'Error finding items for shipment.', level: 'error' });
                setRows([]);
                setSelectedRowIds(new Set());
                return;
            }

            const items = response?.data?.items ?? [];
            const mapped = items.map((item, index) => {
                const { externalSku } = item;
                const itemName = item.name;
                const qty = Number(item?.qtyPicked ?? 0);
                return {
                    id: `${index}-${externalSku || itemName || 'item'}`,
                    externalSku,
                    itemName,
                    qty,
                };
            });

            setRows(mapped);
            setSelectedRowIds(new Set(mapped.map((m) => m.id)));
            setNotification({ text: null, level: null });
        } catch (e) {
            setNotification({ text: 'Error searching by shipment ID', level: 'error' });
            setRows([]);
            setSelectedRowIds(new Set());
        } finally {
            setIsSearching(false);
        }
    }, [shipmentId]);

    const selectedRows = useMemo(() => rows.filter((r) => selectedRowIds.has(r.id)), [rows, selectedRowIds]);

    const canSubmit = useMemo(() => selectedRows.some((r) => Number(r.qty) > 0), [selectedRows]);

    const handleSubmit = useCallback(async () => {
        setIsSubmitting(true);
        try {
            const rowsToSubmit = selectedRows.filter((r) => Number(r.qty) > 0);
            if (rowsToSubmit.length === 0) {
                setNotification({
                    text: 'Enter a quantity greater than 0 for at least one selected item',
                    level: 'error',
                });
                return;
            }
            const data = {
                facilityId: user.facilityID,
                rows: rowsToSubmit.map((r) => ({
                    externalSku: r.externalSku,
                    reason: reasonShipmentRepack,
                    qtyUnits: Number(r.qty),
                    notes: `Shipment: ${shipmentId} ${notes ? `- ${notes}` : ''}`,
                    shipmentId,
                })),
            };
            const res = await inventoryAdjustmentApi.createBulkShrink(data);
            if (res.msg !== 'Success') {
                throw new Error(res.error || res.msg || 'Request failed');
            }
            // Show success/failure summary like AddBulkShrinkModal
            setSubmissionResults(res.data);
        } catch (e) {
            setNotification({ text: e.message || 'Failed to create shrink requests', level: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    }, [selectedRows, reasonShipmentRepack, shipmentId, notes, user, onOpenChange]);

    const allChecked = rows.length > 0 && selectedRowIds.size === rows.length;
    const someChecked = selectedRowIds.size > 0 && selectedRowIds.size < rows.length;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => (open ? null : handleClose())}>
            <DialogContent className="sm:max-w-[800px]">
                <Notifications options={notification} />
                <DialogHeader>
                    <DialogTitle>Shrink Items from a Shipment</DialogTitle>
                </DialogHeader>

                {submissionResults ? (
                    <BulkShrinkSummary results={submissionResults} onClose={() => onOpenChange?.(false, true)} />
                ) : (
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="single">Single Shipment ID</TabsTrigger>
                            <TabsTrigger value="bulk">Bulk Shipment Upload</TabsTrigger>
                        </TabsList>

                        <TabsContent value="single" className="mt-4">
                            <div className="flex flex-col gap-4">
                                <div className="flex items-end gap-3">
                                    <div className="flex-1">
                                        <Label htmlFor="shipment-id">Shipment ID</Label>
                                        <Input
                                            id="shipment-id"
                                            placeholder="Enter shipment ID"
                                            value={shipmentId}
                                            ref={shipmentInputRef}
                                            onChange={(e) => setShipmentId(e.target.value)}
                                        />
                                    </div>
                                    <Button onClick={handleSearch} disabled={isSearching}>
                                        {isSearching ? 'Searching…' : 'Search'}
                                    </Button>
                                    <div className="py-2">
                                        <Label>Reason: {reasonShipmentRepack}</Label>
                                    </div>
                                </div>

                                <div className="max-h-[350px] overflow-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-8">
                                                    <Checkbox
                                                        checked={allChecked}
                                                        aria-checked={someChecked ? 'mixed' : allChecked}
                                                        onCheckedChange={(v) => toggleAll(Boolean(v))}
                                                    />
                                                </TableHead>
                                                <TableHead>External SKU</TableHead>
                                                <TableHead>Item Name</TableHead>
                                                <TableHead className="text-right">Quantity Picked</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {rows.length === 0 ? (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={4}
                                                        className="text-center text-muted-foreground"
                                                    >
                                                        No items
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                rows.map((row) => (
                                                    <TableRow
                                                        key={row.id}
                                                        data-state={selectedRowIds.has(row.id) ? 'selected' : undefined}
                                                    >
                                                        <TableCell>
                                                            <Checkbox
                                                                checked={selectedRowIds.has(row.id)}
                                                                onCheckedChange={() => toggleRow(row.id)}
                                                                aria-label={`Select ${row.itemName}`}
                                                            />
                                                        </TableCell>
                                                        <TableCell className="font-mono">
                                                            {row.externalSku || '-'}
                                                        </TableCell>
                                                        <TableCell>{row.itemName}</TableCell>
                                                        <TableCell className="text-right">
                                                            <Input
                                                                type="number"
                                                                min={0}
                                                                step={1}
                                                                className="w-24 ml-auto text-right"
                                                                value={row.qty}
                                                                onChange={(e) =>
                                                                    handleQtyChange(row.id, e.target.value)
                                                                }
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Label htmlFor="notes" className="w-20">
                                        Notes
                                    </Label>
                                    <Input
                                        id="notes"
                                        placeholder="Enter notes"
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    />
                                </div>
                            </div>
                        </TabsContent>

                        <TabsContent value="bulk" className="mt-4">
                            <BulkShrinkShipmentUpload
                                adjustmentApi={inventoryAdjustmentApi}
                                facilityId={user.facilityID}
                                isOpen={isOpen}
                            />
                        </TabsContent>
                    </Tabs>
                )}

                {!submissionResults && activeTab === 'single' && (
                    <DialogFooter>
                        <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button onClick={handleSubmit} disabled={isSubmitting || !canSubmit}>
                            {isSubmitting ? 'Submitting…' : 'Submit'}
                        </Button>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
}

export { ShrinkShipmentModal };
